// src/pages/MediaPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, TextArea } from '../components/common/Input';
import { useLang } from '../context/LangContext';
import {
  MediaAsset,
  listMedia,
  uploadMedia,
  updateMedia,
  deleteMedia,
  mediaFileUrl,
} from '../api/media';

const PageContainer = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.lg}`};
  background-color: ${({ theme }) => theme.colors.background};
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    text-align: center;
  }
`;

const Title = styled.h1`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 700;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Thumb = styled.div`
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto; /* прижимает кнопки к нижнему краю карточки (карточка — flex-column) */
  padding-top: ${({ theme }) => theme.spacing.md};

  & > * {
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
  }
`;

const SearchWrap = styled.div`
  max-width: 480px;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const TagChip = styled.button`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.75rem;
  padding: 2px 8px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const MetaRow = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
`;

const Empty = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textLight};
  padding: 3rem 0;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  margin: 8px 0 0;
`;

const SuccessText = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalBody = styled(Card)`
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
`;

const FieldLabel = styled.label`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const isImage = (m: string) => m.startsWith('image/');
const isVideo = (m: string) => m.startsWith('video/');

const MediaPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [items, setItems] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [selected, setSelected] = useState<MediaAsset | null>(null);
  const [updating, setUpdating] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result = await listMedia();
      setItems(result);
      setError('');
    } catch {
      setError(t('mediaLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setError('');
    try {
      await uploadMedia(uploadFile, {
        title: uploadTitle,
        description: uploadDesc,
        tags: uploadTags || undefined,
      });
      setShowUpload(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadDesc('');
      setUploadTags('');
      await load();
    } catch {
      setError(t('mediaUploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selected) return;
    setUpdating(true);
    setError('');
    try {
      await updateMedia(selected.id, {
        title: selected.title ?? '',
        description: selected.description ?? '',
        tags: Array.isArray(selected.tags) ? selected.tags.join(',') : '',
      });
      setSelected(null);
      await load();
    } catch {
      setError(t('mediaSaveError'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('mediaDeleteConfirm'))) return;
    setError('');
    try {
      await deleteMedia(id);
      await load();
    } catch {
      setError(t('mediaSaveError'));
    }
  };

  const handleCopyLink = async (id: number) => {
    try {
      await navigator.clipboard.writeText(mediaFileUrl(id));
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1600);
    } catch {
      setError(t('mediaCopyError'));
    }
  };

  const [query, setQuery] = useState('');

  // Клиентская фильтрация по всем полям, которые вписываются в БД при загрузке.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) => {
      const haystack = [
        m.title ?? '',
        m.filename ?? '',
        m.description ?? '',
        m.mime_type ?? '',
        ...(Array.isArray(m.tags) ? m.tags : []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, query]);

  return (
    <PageContainer>
      <Header>
        <Title>{t('mediaTitle')}</Title>
        <Actions>
          <Button variant="outline" size="md" onClick={() => navigate('/tests')}>
            {t('navBack')}
          </Button>
          <Button variant="primary" size="md" onClick={() => setShowUpload(true)}>
            {t('mediaUploadBtn')}
          </Button>
        </Actions>
      </Header>

      {error && <ErrorText>{error}</ErrorText>}

      {!loading && items.length > 0 && (
        <SearchWrap>
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('mediaSearchPlaceholder')}
            fullWidth
          />
        </SearchWrap>
      )}

      {loading ? (
        <Empty>{t('mediaLoading')}</Empty>
      ) : items.length === 0 ? (
        <Empty>{t('mediaEmpty')}</Empty>
      ) : filtered.length === 0 ? (
        <Empty>{t('mediaNoMatch')}</Empty>
      ) : (
        <Grid>
          {filtered.map((m) => (
            <Card key={m.id} style={{ padding: 14, display: 'flex', flexDirection: 'column' }}>
              <Thumb>
                {isImage(m.mime_type) ? (
                  <img src={mediaFileUrl(m.id)} alt={m.title || m.filename} loading="lazy" />
                ) : isVideo(m.mime_type) ? (
                  <video controls muted preload="metadata" style={{ maxWidth: '100%', maxHeight: '100%' }}>
                    <source src={mediaFileUrl(m.id)} type={m.mime_type} />
                  </video>
                ) : (
                  <span style={{ fontSize: '0.8rem', color: '#999' }}>{m.mime_type}</span>
                )}
              </Thumb>
              <div style={{ marginBottom: 4, minHeight: 24 }}>
                <strong style={{ wordBreak: 'break-word', fontSize: '0.95rem' }}>{m.title || m.filename}</strong>
              </div>
              <MetaRow>{m.filename}</MetaRow>
              <MetaRow>{m.mime_type} · {(m.size_bytes / 1024).toFixed(0)} КБ</MetaRow>
              <MetaRow>{m.width && m.height ? `${m.width}×${m.height}` : ''}</MetaRow>
              {Array.isArray(m.tags) && m.tags.length > 0 && (
                <TagRow>
                  {m.tags.map((tag) => (
                    <TagChip key={tag} onClick={() => setQuery(tag)} title={t('mediaTagClickHint')}>
                      #{tag}
                    </TagChip>
                  ))}
                </TagRow>
              )}
              <CardActions>
                <Button variant="outline" size="sm" onClick={() => setSelected({ ...m })}>
                  {t('mediaEditBtn')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleCopyLink(m.id)}>
                  {copiedId === m.id ? t('mediaCopied') : t('mediaCopyLink')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(m.id)}>
                  {t('mediaDeleteBtn')}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Grid>
      )}

      {showUpload && (
        <ModalOverlay onClick={() => setShowUpload(false)}>
          <ModalBody onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t('mediaUploadTitle')}</ModalTitle>
            <FieldLabel>
              <span>{t('mediaFileLabel')}</span>
              <Input type="file" accept="image/*,video/*" onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)} />
            </FieldLabel>
            <FieldLabel>
              <span>{t('mediaTitleLabel')}</span>
              <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} />
            </FieldLabel>
            <FieldLabel>
              <span>{t('mediaDescLabel')}</span>
              <TextArea value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} fullWidth />
            </FieldLabel>
            <FieldLabel>
              <span>{t('mediaTagsLabel')}</span>
              <Input value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} placeholder="tag1, tag2" />
            </FieldLabel>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="outline" size="md" onClick={() => setShowUpload(false)}>
                {t('cancelButton')}
              </Button>
              <Button variant="primary" size="md" disabled={!uploadFile || uploading} onClick={handleUpload}>
                {uploading ? t('mediaUploading') : t('mediaUploadBtn')}
              </Button>
            </div>
          </ModalBody>
        </ModalOverlay>
      )}

      {selected && (
        <ModalOverlay onClick={() => setSelected(null)}>
          <ModalBody onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t('mediaEditTitle')}</ModalTitle>
            <Thumb>
              {isImage(selected.mime_type) ? (
                <img src={mediaFileUrl(selected.id)} alt={selected.filename} />
              ) : isVideo(selected.mime_type) ? (
                <video controls muted style={{ maxWidth: '100%', maxHeight: '100%' }}>
                  <source src={mediaFileUrl(selected.id)} type={selected.mime_type} />
                </video>
              ) : null}
            </Thumb>
            <FieldLabel>
              <span>{t('mediaTitleLabel')}</span>
              <Input value={selected.title ?? ''} onChange={(e) => setSelected({ ...selected, title: e.target.value })} />
            </FieldLabel>
            <FieldLabel>
              <span>{t('mediaDescLabel')}</span>
              <TextArea value={selected.description ?? ''} onChange={(e) => setSelected({ ...selected, description: e.target.value })} fullWidth />
            </FieldLabel>
            <FieldLabel>
              <span>{t('mediaTagsLabel')}</span>
              <Input
                value={Array.isArray(selected.tags) ? selected.tags.join(',') : ''}
                onChange={(e) => setSelected({ ...selected, tags: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
              />
            </FieldLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SuccessText style={{ flex: 1 }}>{mediaFileUrl(selected.id)}</SuccessText>
              <Button variant="outline" size="sm" onClick={() => handleCopyLink(selected.id)}>
                {copiedId === selected.id ? t('mediaCopied') : t('mediaCopyLink')}
              </Button>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="outline" size="md" onClick={() => setSelected(null)}>
                {t('cancelButton')}
              </Button>
              <Button variant="primary" size="md" disabled={updating} onClick={handleSaveEdit}>
                {t('mediaSaveBtn')}
              </Button>
            </div>
          </ModalBody>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default MediaPage;